import { Button } from '@/components/ui/Button';

import type { DirectoryUser } from '../api';
import { MEETING_TYPE_OPTIONS } from '../meetingLabels';
import { DIARIZATION_ENABLED } from '../pipelineFeatures';
import { describeStatus } from '../status';

export type ListFiltersState = {
  search: string;
  clientName: string;
  axiumParticipantId: string;
  meetingType: string;
  status: string;
};

type Props = {
  value: ListFiltersState;
  users: readonly DirectoryUser[];
  onChange: (next: ListFiltersState) => void;
  onSearchChange: (search: string) => void;
  onReset: () => void;
};

const FILTER_STATUS_VALUES: readonly string[] = [
  'CREATED',
  'UPLOADING',
  'UPLOADED',
  'PREPARING',
  'PREPARED',
  'TRANSCRIBING',
  'TRANSCRIBED',
  ...(DIARIZATION_ENABLED ? ['DIARIZING', 'DIARIZED'] : []),
  'READY',
  'FAILED',
  'CANCELED',
];

const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  ...FILTER_STATUS_VALUES.map((value) => ({
    value,
    label: describeStatus(value).label,
  })),
];

function hasActiveFilters(value: ListFiltersState): boolean {
  return Object.values(value).some((item) => item.trim() !== '');
}

export function TranscriptionsFilters({ value, users, onChange, onSearchChange, onReset }: Props) {
  const set = (patch: Partial<ListFiltersState>) => onChange({ ...value, ...patch });
  const canReset = hasActiveFilters(value);

  return (
    <div className="filters-bar" role="search" aria-label="Фильтры журнала встреч">
      <div className="filters-bar__field filters-bar__field--search">
        <label className="filters-bar__label" htmlFor="filter-search">
          Поиск
        </label>
        <input
          id="filter-search"
          className="filters-bar__control"
          type="search"
          placeholder="Клиент или участник"
          value={value.search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>

      <div className="filters-bar__field filters-bar__field--client">
        <label className="filters-bar__label" htmlFor="filter-client">
          Клиент
        </label>
        <input
          id="filter-client"
          className="filters-bar__control"
          type="text"
          placeholder="Все клиенты"
          value={value.clientName}
          onChange={(event) => set({ clientName: event.target.value })}
          autoComplete="off"
        />
      </div>

      <div className="filters-bar__field filters-bar__field--participant">
        <label className="filters-bar__label" htmlFor="filter-participant">
          Участник AXIUM
        </label>
        <select
          id="filter-participant"
          className="filters-bar__control"
          value={value.axiumParticipantId}
          onChange={(event) => set({ axiumParticipantId: event.target.value })}
        >
          <option value="">Все участники</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.displayName}
            </option>
          ))}
        </select>
      </div>

      <div className="filters-bar__field filters-bar__field--type">
        <label className="filters-bar__label" htmlFor="filter-meeting-type">
          Тип встречи
        </label>
        <select
          id="filter-meeting-type"
          className="filters-bar__control"
          value={value.meetingType}
          onChange={(event) => set({ meetingType: event.target.value })}
        >
          <option value="">Все типы</option>
          {MEETING_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="filters-bar__field filters-bar__field--status">
        <label className="filters-bar__label" htmlFor="filter-status">
          Статус
        </label>
        <select
          id="filter-status"
          className="filters-bar__control"
          value={value.status}
          onChange={(event) => set({ status: event.target.value })}
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value || 'all'} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="filters-bar__actions">
        <Button type="button" variant="secondary" disabled={!canReset} onClick={onReset}>
          Сбросить
        </Button>
      </div>
    </div>
  );
}
